import { useState } from "react";
import { compose, intersection, uniq } from "ramda";

import useYardActionEvents from "@hsl/core/hooks/useYardAnalyticsEvents";
import type {
    FundDataWithType,
    ShareClassDataWithType,
} from "@hsl/fund-explorer/metaSchemas";

import useTranslation, { Translate } from "../../../../../hooks/useTranslation";
import {
    type SubscribableAssetCreate,
    type SubscriptionsCreate,
} from "../../../../../openapi";
import { ControlledCollapsible } from "../../../../Collapsible";
import Spinner from "../../../../Spinner";
import { type Frequency } from "./FrequencySelector";
import { getDataWithTypeId as toId } from "./helpers";
import SelectAllToggle from "./SelectAllToggle";
import SelectDocumentsComponent from "./SelectDocumentsComponent";
import { type SelectDocumentContext, type SelectionDocument } from "./types";

type DocumentGroup = "all" | "key";

type Props = {
    funds: FundDataWithType[];
    shareClasses: ShareClassDataWithType[];
    keyDocuments: SelectionDocument[];
    allDocuments: SelectionDocument[];
    subscriptions: SubscriptionsCreate | undefined;
    status: "success" | "loading" | "error";
    onUpdate: SelectDocumentContext["updateSelection"];
    docSubscriptionCreator: SelectDocumentContext["createSubscriptions"];
};

const SelectDocumentsCollapsible = ({
    funds,
    shareClasses,
    keyDocuments,
    allDocuments,
    subscriptions,
    onUpdate,
    docSubscriptionCreator,
    status,
}: Props) => {
    const tx = useTranslation();
    const { accordionEvent, buttonClickEvent, checkboxEvent } =
        useYardActionEvents();
    const yardEventPosition = `Document ${
        subscriptions ? "subscription" : "select"
    } options`;
    const yardEventDetails = funds.map((f) => f.data.data.name).join(",");
    const createSubscriptions = docSubscriptionCreator;
    const uniqIntersection = compose(uniq, intersection);
    const fundIds = funds.map(toId);
    const shareClassIds = shareClasses.map(toId);
    const isAvaialbleFundOrShareClass = (entityId: string) =>
        fundIds.some((id) => id === entityId) ||
        shareClassIds.some((id) => id === entityId);
    const getDocSubsIds = (doc: SelectionDocument) =>
        doc.subscription_data.subsMap
            .filter(([fundId, scId]) =>
                isAvaialbleFundOrShareClass(scId ?? fundId),
            )
            .map(([_, __, subId]) => subId);
    const getSelectedDocSubs = (doc: SelectionDocument) =>
        getDocSubsIds(doc)
            .map((subId) =>
                subscriptions?.find((sub) => sub.subscriptionId === subId),
            )
            .filter((x) => Boolean(x)) as SubscribableAssetCreate[];
    const areAllSelected = (docs: SelectionDocument[]) => {
        const docSubsForEntity = docs.flatMap(getDocSubsIds);
        return (
            uniqIntersection(
                docSubsForEntity,
                (subscriptions ?? []).map((d) => d.subscriptionId),
            ).length === docSubsForEntity.length
        );
    };
    const filterAvailableDocs = (doc: SelectionDocument) =>
        doc.subscription_data.funds.some((fundId) =>
            fundIds.some((id) => id === fundId),
        );
    const availableKeyDocuments = keyDocuments.filter(filterAvailableDocs);
    const availableDocuments = allDocuments.filter(filterAvailableDocs);
    const canSelectAllKeyDocs = subscriptions
        ? !areAllSelected(availableKeyDocuments)
        : true;
    const canSelectAllDocs = subscriptions
        ? !areAllSelected(availableDocuments)
        : true;

    const classes = {
        collapsible: "bg-white",
        label: "px-4 border-b py-3 w-full justify-between font-medium text-sm sm:text-md",
        panel: "px-4 py-2 w-full border-b",
    };
    const [openGroup, setOpenGroup] = useState<DocumentGroup | undefined>(
        "key",
    );

    const handleChange =
        (doc: SelectionDocument) =>
        (arg: { frequency?: Frequency; selected?: boolean }) => {
            const yardEventName = doc.name;
            const subs = createAvailableSubscriptions(doc);
            const selectedSubs = getSelectedDocSubs(doc);
            const subsWithFreq = arg.frequency
                ? selectedSubs.map((sub) => ({
                      ...sub,
                      frequency: arg.frequency,
                  }))
                : subs;
            onUpdate &&
                onUpdate({
                    doc,
                    subs: arg.selected ? subsWithFreq : undefined,
                    funds: fundIds,
                    shareClasses: shareClassIds,
                });
            if (arg.frequency) {
                buttonClickEvent({
                    details: yardEventDetails,
                    name:
                        arg.frequency === "DAILY"
                            ? "Same day"
                            : arg.frequency === "WEEKLY"
                              ? "Add to weekly roundup"
                              : arg.frequency === "MONTHLY"
                                ? "Add to monthly roundup"
                                : "Add to quarterly roundup",
                    position: yardEventPosition,
                    docType: doc.name,
                });
            } else {
                checkboxEvent({
                    name: yardEventName,
                    details: yardEventDetails,
                    position: yardEventPosition,
                });
            }
        };
    const createAvailableSubscriptions = (doc: SelectionDocument) => {
        return createSubscriptions(doc, {
            funds: fundIds,
            shareClasses: shareClassIds,
        });
    };

    const handleSelectAll = (selectAll: boolean, type: "all" | "key") => {
        const subs =
            type === "all"
                ? availableDocuments.flatMap(createAvailableSubscriptions)
                : availableKeyDocuments.flatMap(createAvailableSubscriptions);
        const name = selectAll ? "Select all" : "Clear all";
        if (selectAll) {
            onUpdate &&
                onUpdate({
                    subs,
                });
        } else {
            onUpdate &&
                onUpdate({
                    subs,
                    remove: true,
                });
        }
        buttonClickEvent({
            name,
            details: yardEventDetails,
            position: yardEventPosition,
        });
    };

    const isSelected = (doc: SelectionDocument) => {
        const subsIds = getDocSubsIds(doc);
        return subscriptions
            ? subscriptions.some((sub) =>
                  subsIds.some((subId) => subId === sub.subscriptionId),
              )
            : false;
    };

    const getDocFreq = (doc: SelectionDocument) => {
        const subsIds = getDocSubsIds(doc);
        const [firstSub] = (subscriptions ?? []).filter((sub) =>
            subsIds.includes(`${sub.subscriptionId}`),
        );
        return firstSub?.frequency ?? "DAILY";
    };

    const selectToggleClasses =
        "text-xs sm:text-sm text-core-blue underline mb-2 font-medium cursor-pointer";
    const toggleOpenGroup = (type: "key" | "all") => {
        const isOpen = openGroup === type;
        accordionEvent({
            name:
                type === "all"
                    ? "All documents by category"
                    : "Key documents and data files",
            details: yardEventDetails,
            type: isOpen ? "close" : "open",
            position: yardEventPosition,
        });
        setOpenGroup(isOpen ? undefined : type);
    };

    switch (status) {
        case "success":
            return (
                <div className="mt-4 bg-white shadow">
                    <ControlledCollapsible
                        labelClassNames={classes.label}
                        label={tx("Key documents and data files").result}
                        withChevron
                        isOpen={openGroup === "key"}
                        onClick={() => toggleOpenGroup("key")}
                    >
                        <div className={classes.panel}>
                            <SelectAllToggle
                                onLabel={<Translate>Clear all</Translate>}
                                offLabel={<Translate>Select all</Translate>}
                                onChange={(state) =>
                                    handleSelectAll(state, "key")
                                }
                                value={!canSelectAllKeyDocs}
                                className={selectToggleClasses}
                            />
                            {availableKeyDocuments.map((doc, i) => (
                                <SelectDocumentsComponent
                                    setId={doc.setId}
                                    key={`doc-${i}`}
                                    level={
                                        doc.scSelector ? "SHARE_CLASS" : "FUND"
                                    }
                                    allDocuments={allDocuments}
                                    label={doc.name}
                                    onChange={handleChange(doc)}
                                    selected={isSelected(doc)}
                                    funds={funds}
                                    shareClasses={shareClasses}
                                    frequency={getDocFreq(doc)}
                                />
                            ))}
                        </div>
                    </ControlledCollapsible>
                    <ControlledCollapsible
                        labelClassNames={classes.label}
                        label={tx("All documents by category").result}
                        withChevron
                        isOpen={openGroup === "all"}
                        onClick={() => toggleOpenGroup("all")}
                    >
                        <div className={classes.panel}>
                            <SelectAllToggle
                                onLabel={<Translate>Clear all</Translate>}
                                offLabel={<Translate>Select all</Translate>}
                                onChange={(state) =>
                                    handleSelectAll(state, "all")
                                }
                                value={!canSelectAllDocs}
                                className={selectToggleClasses}
                            />

                            {availableDocuments.map((doc, i) => (
                                <SelectDocumentsComponent
                                    setId={doc.setId}
                                    key={`doc-${i}`}
                                    level={
                                        doc.scSelector ? "SHARE_CLASS" : "FUND"
                                    }
                                    allDocuments={allDocuments}
                                    label={doc.name}
                                    onChange={handleChange(doc)}
                                    selected={isSelected(doc)}
                                    funds={funds}
                                    shareClasses={shareClasses}
                                    frequency={getDocFreq(doc)}
                                />
                            ))}
                        </div>
                    </ControlledCollapsible>
                </div>
            );
        case "loading":
            return <Spinner className="my-16" />;
        case "error":
            return (
                <p className="my-16 w-full text-center">An error occurred</p>
            );
    }
};

export default SelectDocumentsCollapsible;
